import React, { useState, lazy } from 'react';
import { PaystackButton } from '../components/PaystackButton';
import { Check, Mail, Users, Target, TrendingUp, Award, Clock, MapPin, Globe, Laptop, Wifi, CheckCircle, FileText, Briefcase } from 'lucide-react';
export function EmployabilitySkillsPage() {
  const [email, setEmail] = useState('');
  const [showEmailInput, setShowEmailInput] = useState(false);
  const handleEnrollClick = () => {
    setShowEmailInput(true);
  };
  const handlePaymentSuccess = (reference: string) => {
    alert(`Payment successful! Reference: ${reference}. We'll send you enrollment details shortly.`);
    setEmail('');
    setShowEmailInput(false);
  };
  return <div className="flex flex-col min-h-screen bg-white" data-id="element-862">
      <main className="flex-grow pt-20" data-id="element-863">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 py-8" data-id="element-864">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-865">
            {/* Main Content */}
            <div className="lg:col-span-2" data-id="element-866">
              {/* Hero */}
              <section className="mb-12" data-id="element-867">
                <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 mb-4" data-id="element-868">
                  Employability Skills Course
                </h1>
                <p className="text-2xl md:text-3xl font-semibold text-blue-600 mb-6" data-id="element-869">
                  Land the Job. Thrive at Work.
                </p>

                <div className="rounded-2xl overflow-hidden mb-6 shadow-xl" data-id="element-870">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/b4MYSaEckGRDbEPwpZELiK/employab.png" alt="Employability Skills Training" loading="lazy" className="w-full h-auto object-cover hover:scale-105 transition-transform duration-500" data-id="element-871" />
                </div>
              </section>

              {/* About */}
              <section className="mb-12" data-id="element-872">
                <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-873">
                  About the Course
                </h2>
                <div className="prose prose-lg max-w-none" data-id="element-874">
                  <p className="text-gray-700 leading-relaxed mb-4" data-id="element-875">
                    A degree opens the door, but employers want more — they want
                    job-ready candidates. The Employability Skills Course by
                    SkillzHQ Academy is an 8-week, hands-on online program that
                    bridges the gap between education and the workplace.
                  </p>
                  <p className="text-gray-700 leading-relaxed" data-id="element-876">
                    This course is designed to help you move from theory to
                    practice, equipping you with the essential skills,
                    confidence, and mindset to secure the right job and grow in
                    it. Through interactive lessons, case studies, and guided
                    exercises, you'll learn how to position yourself as the
                    professional employers want to hire — and keep.
                  </p>
                </div>
              </section>

              {/* Target Audience */}
              <section className="mb-12" data-id="element-877">
                <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-878">
                  Who Should Enroll?
                </h2>
                <p className="text-gray-700 mb-6" data-id="element-879">
                  This course is tailored for:
                </p>
                <div className="space-y-4" data-id="element-880">
                  {[{
                  icon: Users,
                  text: 'Recent Graduates preparing to enter the workforce with confidence'
                }, {
                  icon: Award,
                  text: 'NYSC Members seeking to maximize their service year for career readiness'
                }, {
                  icon: Briefcase,
                  text: 'Entry-Level Professionals aiming to sharpen workplace skills'
                }, {
                  icon: TrendingUp,
                  text: 'Career Changers wanting to refresh and repackage their professional profile'
                }].map((item, index) => {
                  const Icon = item.icon;
                  return <div key={index} className="flex items-start gap-3 bg-blue-50 p-4 rounded-lg" data-id="element-881">
                        <Icon className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" data-id="element-882" />
                        <p className="text-gray-700" data-id="element-883">{item.text}</p>
                      </div>;
                })}
                </div>
              </section>

              {/* What You'll Learn */}
              <section className="mb-12" data-id="element-884">
                <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-885">
                  What You'll Learn
                </h2>
                <p className="text-gray-700 mb-6" data-id="element-886">
                  By the end of this course, you'll master:
                </p>

                <div className="bg-gradient-to-br from-blue-50 to-green-50 rounded-2xl p-8" data-id="element-887">
                  <div className="space-y-4" data-id="element-888">
                    {['Crafting standout CVs and cover letters tailored to your desired roles', 'Building a strong LinkedIn profile and leveraging online networking', 'Practicing effective interview techniques through mock sessions and feedback', 'Developing workplace communication, collaboration, and etiquette', 'Understanding employer expectations — and how to exceed them', 'Goal-setting, accountability, and personal brand building', 'Critical thinking, adaptability, and time management', 'Navigating workplace challenges with confidence and initiative'].map((skill, index) => <div key={index} className="flex items-start gap-3" data-id="element-889">
                        <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" data-id="element-890" />
                        <p className="text-gray-700" data-id="element-891">{skill}</p>
                      </div>)}
                  </div>
                </div>
              </section>

              {/* Course Details */}
              <section className="mb-12" data-id="element-892">
                <h2 className="text-3xl font-bold text-gray-900 mb-8" data-id="element-893">
                  Course Format & Requirements
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-894">
                  <div className="bg-white rounded-xl p-6 border-2 border-blue-100" data-id="element-895">
                    <Clock className="w-8 h-8 text-blue-600 mb-4" data-id="element-896" />
                    <h3 className="font-bold text-gray-900 mb-2" data-id="element-897">Duration</h3>
                    <p className="text-gray-700" data-id="element-898">8 weeks</p>
                  </div>
                  <div className="bg-white rounded-xl p-6 border-2 border-green-100" data-id="element-899">
                    <MapPin className="w-8 h-8 text-green-600 mb-4" data-id="element-900" />
                    <h3 className="font-bold text-gray-900 mb-2" data-id="element-901">Location</h3>
                    <p className="text-gray-700" data-id="element-902">100% Virtual</p>
                  </div>
                  <div className="bg-white rounded-xl p-6 border-2 border-blue-100" data-id="element-903">
                    <Users className="w-8 h-8 text-blue-600 mb-4" data-id="element-904" />
                    <h3 className="font-bold text-gray-900 mb-2" data-id="element-905">Mode</h3>
                    <p className="text-gray-700" data-id="element-906">
                      Self-paced with live cohort interactions
                    </p>
                  </div>
                </div>

                {/* Requirements */}
                <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 border border-gray-200" data-id="element-907">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-908">
                    Requirements
                  </h3>
                  <div className="space-y-4" data-id="element-909">
                    <div className="flex items-start gap-3" data-id="element-910">
                      <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" data-id="element-911" />
                      <p className="text-gray-700" data-id="element-912">
                        Commitment to coursework throughout the 8-week duration
                      </p>
                    </div>
                    <div className="flex items-start gap-3" data-id="element-913">
                      <Laptop className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" data-id="element-914" />
                      <p className="text-gray-700" data-id="element-915">
                        Reliable access to a PC (Windows 10+ / Mac OS X latest
                        version); mobile devices may be used for secondary
                        access
                      </p>
                    </div>
                    <div className="flex items-start gap-3" data-id="element-916">
                      <Wifi className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" data-id="element-917" />
                      <p className="text-gray-700" data-id="element-918">
                        Strong internet connection (4G/LTE/5G recommended)
                      </p>
                    </div>
                    <div className="flex items-start gap-3" data-id="element-919">
                      <Globe className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" data-id="element-920" />
                      <p className="text-gray-700" data-id="element-921">
                        Compatible browsers: Google Chrome, Mozilla Firefox,
                        Microsoft Edge, Safari
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Career Outcomes */}
              <section className="mb-12" data-id="element-922">
                <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-923">
                  Career Outcomes & Benefits
                </h2>
                <p className="text-gray-700 mb-6" data-id="element-924">
                  Completing this program positions you to:
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-925">
                  {[{
                  icon: Award,
                  text: 'Earn a SkillzHQ Job Readiness Certificate recognized by employers'
                }, {
                  icon: FileText,
                  text: 'Graduate with a full career toolkit (resume, LinkedIn profile, interview pitch, personal roadmap)'
                }, {
                  icon: Target,
                  text: 'Step into job applications and interviews with clarity and confidence'
                }, {
                  icon: TrendingUp,
                  text: 'Develop soft skills that consistently top employer wish-lists'
                }, {
                  icon: Users,
                  text: 'Gain access to career coaches, hiring leads, and an active peer network'
                }, {
                  icon: Briefcase,
                  text: 'Stand out as a competent, confident early-career professional ready to grow'
                }].map((item, index) => {
                  const Icon = item.icon;
                  return <div key={index} className="bg-white rounded-xl p-6 border-2 border-blue-100 hover:border-blue-300 hover:shadow-lg transition-all duration-300" data-id="element-926">
                        <Icon className="w-8 h-8 text-blue-600 mb-4" data-id="element-927" />
                        <p className="text-gray-700" data-id="element-928">{item.text}</p>
                      </div>;
                })}
                </div>
              </section>

              {/* Instructor */}
              <section className="mb-12" data-id="element-929">
                <h2 className="text-3xl font-bold text-gray-900 mb-8" data-id="element-930">
                  Your Instructor
                </h2>
                <div className="bg-gradient-to-br from-blue-50 to-white rounded-2xl p-8 border border-blue-100" data-id="element-931">
                  <div className="flex items-start gap-6" data-id="element-932">
                    <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/fgM76ZqeykjR9P5o8eiPfX/IMG_0273.jpg" alt="Ms. Precious Imuwahen Ogbomo" loading="lazy" className="w-32 h-32 rounded-2xl object-cover" data-id="element-933" />
                    <div data-id="element-934">
                      <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-935">
                        Ms. Precious Imuwahen Ogbomo
                      </h3>
                      <p className="text-gray-700 leading-relaxed" data-id="element-936">
                        An accomplished sales and learning strategist with over
                        15 years of experience working with global brands and
                        training thousands of young professionals across Africa.
                        She brings both industry expertise and a passion for
                        youth empowerment, ensuring learners leave with
                        practical, market-relevant skills.
                      </p>
                    </div>
                  </div>
                </div>
              </section>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1" data-id="element-937">
              <div className="lg:sticky lg:top-24" data-id="element-938">
                <div className="bg-white rounded-2xl shadow-xl border-2 border-blue-100 p-8" data-id="element-939">
                  <div className="mb-6" data-id="element-940">
                    <div className="flex items-baseline gap-2 mb-2" data-id="element-941">
                      <span className="text-4xl font-bold text-gray-900" data-id="element-942">
                        ₦100,000
                      </span>
                      <span className="text-gray-600" data-id="element-943">≈ $100</span>
                    </div>
                    <p className="text-sm text-gray-600" data-id="element-944">
                      One-time payment for full course access
                    </p>
                  </div>

                  <div className="space-y-4 mb-8" data-id="element-945">
                    {['8-week comprehensive program', 'Interactive lessons and case studies', 'Mock interview sessions', 'CV and LinkedIn profile building', 'Job Readiness Certificate', 'Career coaching and peer network'].map((item, index) => <div key={index} className="flex items-start gap-3" data-id="element-946">
                        <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-947" />
                        <span className="text-gray-700" data-id="element-948">{item}</span>
                      </div>)}
                  </div>

                  {!showEmailInput ? <button onClick={handleEnrollClick} className="w-full bg-blue-600 text-white font-semibold py-4 px-6 rounded-full hover:bg-blue-700 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 transform" data-id="element-949">
                      Enroll Now - Limited Seats
                    </button> : <div className="space-y-4" data-id="element-950">
                      <div data-id="element-951">
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-952">
                          Enter your email to proceed
                        </label>
                        <input type="email" id="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="your.email@example.com" className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none transition-colors duration-200" required data-id="element-953" />
                      </div>
                      <PaystackButton amount={100000} email={email} onSuccess={handlePaymentSuccess} variant="primary" fullWidth data-id="element-954">
                        Pay ₦100,000
                      </PaystackButton>
                      <button onClick={() => setShowEmailInput(false)} className="w-full text-gray-600 text-sm hover:text-gray-800 transition-colors duration-200" data-id="element-955">
                        Cancel
                      </button>
                    </div>}

                  <div className="mt-6 pt-6 border-t border-gray-200" data-id="element-956">
                    <div className="bg-green-50 rounded-lg p-4 border border-green-200" data-id="element-957">
                      <p className="text-sm text-green-900 font-medium mb-2" data-id="element-958">
                        🎓 Special Group Rates Available
                      </p>
                      <p className="text-xs text-green-800 mb-3" data-id="element-959">
                        Organizations, schools, or groups purchasing in bulk can
                        get tailored pricing.
                      </p>
                      <a href="mailto:myskillzhq@gmail.com" className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors duration-200" data-id="element-960">
                        <Mail className="w-4 h-4" data-id="element-961" />
                        myskillzhq@gmail.com
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>;
}